import { Checkbox, Input, Select } from '@cloudflare/kumo'
import { individualTaxIdTypesForCountry, taxIdCountries } from '@hub-wifi/shared'
import { useTranslation } from '../../i18n'
import {
  PASSPORT_TAX_ID_TYPE,
  SINGLE_INDIVIDUAL_TAX_ID_TYPE,
  TOGGLEABLE_TAX_ID_TYPE_ID,
} from '../../lib/taxIdFields'
import './enroll.css'

/** Countries that offer at least one individual (natural-person) tax-id
 * type — enrollment only ever accepts individuals (Brazil: CPF), never
 * company/VAT/employer types. Data-driven off `individualTaxIdTypesForCountry`
 * so this adapts automatically if more individual types/countries are added
 * upstream in `@hub-wifi/shared`. */
export const INDIVIDUAL_TAX_ID_COUNTRIES = taxIdCountries().filter(
  (country) => individualTaxIdTypesForCountry(country).length > 0,
)
export const DEFAULT_COUNTRY = INDIVIDUAL_TAX_ID_COUNTRIES[0] ?? ''
export const DEFAULT_TYPE = individualTaxIdTypesForCountry(DEFAULT_COUNTRY)[0]?.id ?? ''

interface TaxIdFieldsProps {
  taxIdCountry: string
  taxIdType: string
  taxIdValue: string
  error?: string
  /** CPF input — the page applies the digit mask. */
  onCpfChange: (raw: string) => void
  /** Passport / generic tax-id input (and the country/type selects). */
  onValueChange: (value: string) => void
  onCountryChange: (country: string) => void
  onTypeChange: (type: string) => void
  /** "I don't have a CPF" toggled (true = switch to passport). */
  onNoCpfChange: (noCpf: boolean) => void
}

/** CPF field with an "I don't have a CPF" checkbox that swaps it for a
 * passport field; falls back to country/type selects when more than one
 * individual tax-id type exists. Shared by both enrollment pages. */
export function TaxIdFields(props: TaxIdFieldsProps) {
  const { t } = useTranslation()
  const { taxIdCountry, taxIdType, taxIdValue, error } = props

  if (!SINGLE_INDIVIDUAL_TAX_ID_TYPE) {
    return (
      <>
        <Select
          label={t('enroll.fields.taxIdCountry')}
          value={taxIdCountry}
          onValueChange={(v) => v && props.onCountryChange(v)}
        >
          {INDIVIDUAL_TAX_ID_COUNTRIES.map((country) => (
            <Select.Option key={country} value={country}>
              {country}
            </Select.Option>
          ))}
        </Select>

        <Select
          label={t('enroll.fields.taxIdType')}
          value={taxIdType}
          onValueChange={(v) => v && props.onTypeChange(v)}
        >
          {individualTaxIdTypesForCountry(taxIdCountry).map((type) => (
            <Select.Option key={type.id} value={type.id}>
              {type.label}
            </Select.Option>
          ))}
        </Select>

        <Input
          className="enroll-input"
          label={t('enroll.fields.taxIdValue')}
          value={taxIdValue}
          onChange={(e) => props.onValueChange(e.target.value)}
          error={error}
          inputMode={taxIdType === 'br_cpf' ? 'numeric' : undefined}
          enterKeyHint="next"
          required
        />
      </>
    )
  }

  const noCpf = taxIdType === TOGGLEABLE_TAX_ID_TYPE_ID && !!PASSPORT_TAX_ID_TYPE
  return (
    <>
      {noCpf && PASSPORT_TAX_ID_TYPE ? (
        <Input
          className="enroll-input"
          label={PASSPORT_TAX_ID_TYPE.label}
          description={t('enroll.fields.taxIdValueHint', { example: PASSPORT_TAX_ID_TYPE.example })}
          value={taxIdValue}
          onChange={(e) => props.onValueChange(e.target.value)}
          error={error}
          autoCapitalize="characters"
          enterKeyHint="next"
          required
        />
      ) : (
        <Input
          className="enroll-input"
          label={SINGLE_INDIVIDUAL_TAX_ID_TYPE.label}
          description={t('enroll.fields.taxIdValueHint', {
            example: SINGLE_INDIVIDUAL_TAX_ID_TYPE.example,
          })}
          value={taxIdValue}
          onChange={(e) => props.onCpfChange(e.target.value)}
          error={error}
          inputMode="numeric"
          enterKeyHint="next"
          required
        />
      )}
      {PASSPORT_TAX_ID_TYPE && (
        <div className="enroll-check-row">
          <Checkbox
            label={t('enroll.fields.taxIdSwitchToPassport')}
            checked={noCpf}
            onCheckedChange={props.onNoCpfChange}
          />
        </div>
      )}
    </>
  )
}
